import { ensure, isNil } from "lopermut"
import * as React from "react"
import { createPortal } from "react-dom"
import * as RechartsPrimitive from "recharts"
import { Button } from "../atoms/Button"
import { cn } from "../utils"

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: "", dark: ".dark" } as const

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode
    icon?: React.ComponentType
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  )
}

type ChartContextProps = {
  config: ChartConfig
  containerRef: React.RefObject<HTMLDivElement | null>
}

const ChartContext = React.createContext<ChartContextProps | null>(null)

function useChart() {
  const context = React.useContext(ChartContext)

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />")
  }

  return context
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config?: ChartConfig
    children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>["children"]
  }
>(({ id, className, children, config = {}, ...props }, ref) => {
  const uniqueId = React.useId()
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Merge the forwarded ref with our internal ref
  React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement, [])

  return (
    <ChartContext.Provider value={{ config, containerRef }}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex justify-center text-xs [&_.recharts-square[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
          className,
        )}
        ref={containerRef}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  )
})

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(([, config]) => config.theme || config.color)

  if (!colorConfig.length) {
    return null
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color = itemConfig.theme?.[theme as keyof typeof itemConfig.theme] || itemConfig.color
    return color ? `  --color-${key}: ${color};` : null
  })
  .join("\n")}
}
`,
          )
          .join("\n"),
      }}
    />
  )
}

const ChartTooltip = RechartsPrimitive.Tooltip

function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = "square",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
  payloadOrder,
  coordinate,
}: Partial<
  Pick<
    RechartsPrimitive.TooltipContentProps,
    "active" | "payload" | "label" | "coordinate" | "formatter" | "labelFormatter"
  >
> & {
  className?: string
  labelClassName?: string
  color?: string
  hideLabel?: boolean
  hideIndicator?: boolean
  indicator?: "line" | "square" | "dashed"
  nameKey?: string
  labelKey?: string
  payloadOrder?: string[]
}) {
  const { config, containerRef } = useChart()
  const tooltipRef = React.useRef<HTMLDivElement>(null)
  const [position, setPosition] = React.useState<{ left: number; top: number } | null>(null)

  // Ensure tooltip items follow the explicit order or the order defined in ChartConfig
  const orderedPayload = React.useMemo(() => {
    if (!payload?.length) return payload

    const orderedKeys = payloadOrder ?? Object.keys(config)

    function resolveConfigKey(item: unknown) {
      if (typeof item !== "object" || item === null) return "value"

      const anyItem = item as Record<string, unknown>
      const baseKey = nameKey || (anyItem.name as string) || (anyItem.dataKey as string) || "value"

      // Try to find the key in orderedKeys that matches the baseKey
      const matchingKey = orderedKeys.find((key) => key === baseKey)
      if (matchingKey) return matchingKey

      // If no direct match, try to find a key that contains the baseKey
      const fuzzyMatch = orderedKeys.find((key) => key.includes(baseKey))
      if (fuzzyMatch) return fuzzyMatch

      return baseKey
    }

    const orderOf = (item: unknown) => {
      const key = resolveConfigKey(item)
      const idx = orderedKeys.indexOf(key)
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx
    }

    return [...payload].sort((a, b) => orderOf(a) - orderOf(b))
  }, [payload, config, nameKey, payloadOrder])

  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null
    }

    const [item] = payload
    const key = `${labelKey || item?.dataKey || item?.name || "value"}`
    const itemConfig = getPayloadConfigFromPayload(config, item, key)
    const value =
      !labelKey && typeof label === "string"
        ? config[label as keyof typeof config]?.label || label
        : itemConfig?.label

    if (labelFormatter) {
      return (
        <div className={cn("font-medium", labelClassName)}>{labelFormatter(value, payload)}</div>
      )
    }

    if (!value) {
      return null
    }

    return <div className={cn("font-medium", labelClassName)}>{value}</div>
  }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey])

  // Calculate portal position
  React.useLayoutEffect(() => {
    if (!active || !coordinate || typeof document === "undefined") {
      setPosition(null)
      return
    }

    // Use the container ref from context to get the correct chart container
    const chartContainer = containerRef.current
    if (!chartContainer) {
      setPosition(null)
      return
    }

    const containerRect = chartContainer.getBoundingClientRect()
    const tooltipRect = tooltipRef.current?.getBoundingClientRect()

    // Convert chart coordinates to viewport coordinates
    let left = containerRect.left + (coordinate.x || 0)
    let top = containerRect.top + (coordinate.y || 0)

    // Adjust for tooltip size to position above/beside cursor
    if (tooltipRect) {
      const viewportPadding = 8
      const verticalOffset = 8
      const horizontalOffset = 8

      // Position above cursor by default
      top = top - tooltipRect.height - verticalOffset

      // If tooltip goes above viewport, position below
      if (top < viewportPadding) {
        top = containerRect.top + (coordinate.y || 0) + verticalOffset
      }

      // If tooltip goes right of viewport, position to the left
      if (left + tooltipRect.width > window.innerWidth - viewportPadding) {
        left = containerRect.left + (coordinate.x || 0) - tooltipRect.width - horizontalOffset
      }

      // Ensure tooltip doesn't go left of viewport
      if (left < viewportPadding) {
        left = viewportPadding
      }
    }

    setPosition({ left, top })
  }, [active, coordinate, containerRef, payload])

  if (!active || !payload?.length) {
    return null
  }

  const nestLabel = payload.length === 1 && indicator !== "square"

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={cn(
        "border-border/50 bg-background grid min-w-[12rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl z-50",
        position && "fixed",
        className,
      )}
      style={
        position
          ? {
              left: `${position.left}px`,
              top: `${position.top}px`,
            }
          : undefined
      }
    >
      {!nestLabel ? tooltipLabel : null}
      <div className="grid gap-1.5">
        {orderedPayload?.map((item, index) => {
          const key = `${nameKey || item.name || item.dataKey || "value"}`
          const itemConfig = getPayloadConfigFromPayload(config, item, key)
          const indicatorColor = color || item.payload?.fill || item.color

          return (
            <div
              key={index}
              className={cn(
                "[&>svg]:text-muted-foreground flex w-full flex-wrap shrink-0 gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5",
                indicator === "square" && "items-center",
              )}
            >
              {formatter && item?.value !== undefined && item.name ? (
                formatter(item.value, item.name, item, index, item.payload)
              ) : (
                <>
                  {itemConfig?.icon ? (
                    <itemConfig.icon />
                  ) : (
                    !hideIndicator && (
                      <div
                        className={cn(
                          "shrink-0 rounded-full border-(--color-border) bg-(--color-bg)",
                          {
                            "h-2.5 w-2.5": indicator === "square",
                            "w-1": indicator === "line",
                            "w-0 border-[1.5px] border-dashed bg-transparent":
                              indicator === "dashed",
                            "my-0.5": nestLabel && indicator === "dashed",
                          },
                        )}
                        style={
                          {
                            "--color-bg": indicatorColor,
                            "--color-border": indicatorColor,
                          } as React.CSSProperties
                        }
                      />
                    )
                  )}
                  <div
                    className={cn(
                      "grid flex-1 grid-cols-[1fr_auto] gap-x-3 leading-none",
                      nestLabel ? "items-end" : "items-center",
                    )}
                  >
                    <div className="grid gap-1.5">
                      {nestLabel ? tooltipLabel : null}
                      <span className="text-muted-foreground">
                        {itemConfig?.label || item.name}
                      </span>
                    </div>
                    {!isNil(item.value) && (
                      <span className="text-foreground font-mono font-medium tabular-nums">
                        {item.value.toLocaleString()}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )

  // Always render via portal to document.body (so it doesn't render in SVG)
  if (active && position && typeof document !== "undefined") {
    return createPortal(tooltipContent, document.body) as unknown as React.ReactElement
  }

  return null
}

const ChartLegend = RechartsPrimitive.Legend

type MaxItemsProps =
  | {
      maxItems?: undefined
    }
  | {
      maxItems: number
      viewMoreLabel: string
      viewLessLabel: string
    }

type ChartLegendContentProps = {
  hideIcon?: boolean
  nameKey?: string
  onLegendItemClick?: (dataKey: string) => void
  activeKeys?: Set<string>
  hideInactive?: boolean
} & MaxItemsProps

type LegendPayloadItem = {
  name: string
  color: string
  value: string | undefined
  dataKey: string
  onClick?: () => void
}

function ChartLegendContent({
  className,
  hideIcon = false,
  payload,
  verticalAlign = "bottom",
  nameKey,
  maxItems = undefined,
  onLegendItemClick,
  activeKeys,
  hideInactive = false,
  ...props
}: React.ComponentProps<"div"> &
  Pick<RechartsPrimitive.DefaultLegendContentProps, "payload" | "verticalAlign"> &
  ChartLegendContentProps) {
  const { config } = useChart()
  const [showAll, setShowAll] = React.useState(false)

  // Ensure legend items follow the order defined in ChartConfig, but reversed
  const orderedPayload: LegendPayloadItem[] = React.useMemo(() => {
    if (!payload || payload.length === 0) return []

    const orderedKeys = Object.keys(config)
    let orderedPayload = [...payload]
      .sort(
        (a, b) =>
          orderedKeys.indexOf(a.dataKey as string) - orderedKeys.indexOf(b.dataKey as string),
      )
      .reverse()
      .map((item) => ({
        name: item.dataKey as string,
        color: item.color as string,
        value: item.value,
        dataKey: item.dataKey as string,
      }))

    if (typeof maxItems === "number" && maxItems) {
      const viewMoreLabel = ensure("viewMoreLabel" in props ? props.viewMoreLabel : undefined)
      const viewLessLabel = ensure("viewLessLabel" in props ? props.viewLessLabel : undefined)

      if (!showAll) {
        orderedPayload = orderedPayload.slice(0, maxItems)
        if (orderedPayload.length < payload.length) {
          const viewMoreItem: LegendPayloadItem = {
            name: viewMoreLabel || "View more",
            color: "white",
            value: "view-more",
            dataKey: "view-more",
            onClick: () => setShowAll(true),
          }
          orderedPayload.push(viewMoreItem)
        }
      } else {
        const viewLessItem: LegendPayloadItem = {
          name: viewLessLabel,
          color: "white",
          value: "view-less",
          dataKey: "view-less",
          onClick: () => setShowAll(false),
        }
        orderedPayload.push(viewLessItem)
      }
    }
    return orderedPayload
  }, [payload, config, maxItems, props, showAll])

  if (orderedPayload.length === 0) {
    return null
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-x-4 gap-y-1 max-w-full flex-wrap",
        verticalAlign === "top" ? "pb-3" : "pt-3",
        className,
      )}
    >
      {orderedPayload.map((item) => {
        const key = `${nameKey || item.dataKey || "value"}`
        const itemConfig = getPayloadConfigFromPayload(config, item, key)
        const isViewMore = item.dataKey === "view-more"
        const isViewLess = item.dataKey === "view-less"
        const isClickable = isViewMore || isViewLess

        if (isClickable) {
          return (
            <Button
              key={item.value}
              variant="ghost"
              size="sm"
              onClick={item.onClick}
              className="h-auto px-2 py-1"
            >
              {itemConfig?.label || item.name}
            </Button>
          )
        }

        const isActive = activeKeys ? activeKeys.has(item.dataKey) : true
        const isHidden = hideInactive && !isActive

        if (isHidden) {
          return null
        }

        const handleClick = onLegendItemClick
          ? () => {
              onLegendItemClick(item.dataKey)
            }
          : undefined

        return (
          <button
            key={item.value}
            type="button"
            onClick={handleClick}
            className={cn(
              "[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3",
              onLegendItemClick && "cursor-pointer transition-opacity hover:opacity-80",
              !isActive && "opacity-50",
            )}
            aria-label={`Toggle ${itemConfig?.label || item.name}`}
          >
            {itemConfig?.icon && !hideIcon ? (
              <itemConfig.icon />
            ) : (
              <div
                className="h-2 w-2 shrink-0 rounded-full"
                style={{
                  backgroundColor: item.color,
                }}
              />
            )}
            {itemConfig?.label || item.name}
          </button>
        )
      })}
    </div>
  )
}

// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (typeof payload !== "object" || payload === null) {
    return undefined
  }

  const payloadPayload =
    "payload" in payload && typeof payload.payload === "object" && payload.payload !== null
      ? payload.payload
      : undefined

  let configLabelKey: string = key

  if (key in payload && typeof payload[key as keyof typeof payload] === "string") {
    configLabelKey = payload[key as keyof typeof payload] as string
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[key as keyof typeof payloadPayload] as string
  }

  return configLabelKey in config ? config[configLabelKey] : config[key as keyof typeof config]
}

export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
}
